import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LogOut, Search, MessageCircle, RefreshCw, Users, TrendingUp, Zap, Clock, X, Download, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const LABELS = {
  tipo_ingreso: { facturo: "Facturo al SAT", spei_terminal: "SPEI / terminal", combinacion: "Combinación", efectivo: "Efectivo sin facturar" },
  tiempo_operando: { menos_1: "< 1 año", "1_2": "1-2 años", "2_5": "2-5 años", mas_5: "> 5 años" },
  monto: { "100k_500k": "$100K–$500K", "500k_2m": "$500K–$2M", "2m_10m": "$2M–$10M", mas_10m: "> $10M" },
  historial_buro: { al_corriente: "Al corriente", atraso_menor: "Atraso menor", vencidas_activas: "Vencidas activas", no_se: "No lo sabe" },
  garantia: { si: "Sí, tiene propiedad", no: "Sin garantía", "": "—" },
  urgencia: { urgente: "Urgente", mes: "Próximo mes", evaluando: "Evaluando" },
};
const RESULT_BADGE = {
  alto_potencial: { t: "Alto potencial", c: "#00C853" },
  requiere_analisis: { t: "Requiere análisis", c: "#FFB800" },
  no_califica_aun: { t: "Trabajar perfil", c: "#52525B" },
};
const ESTADOS = ["nuevo", "contactado", "en_proceso", "ganado", "perdido"];
const ESTADO_LABEL = { nuevo: "Nuevo", contactado: "Contactado", en_proceso: "En proceso", ganado: "Ganado", perdido: "Perdido" };

const WA = "523344065965";

export default function Admin() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todos");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data); setStats(s.data);
    } catch (e) { toast.error("Error al cargar los datos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateEstado = async (id, estado) => {
    try {
      await api.patch(`/leads/${id}`, { estado });
      setLeads((prev) => prev.map((l) => (l._id === id ? { ...l, estado } : l)));
      if (selected?._id === id) setSelected((s) => ({ ...s, estado }));
      toast.success("Estado actualizado");
    } catch (e) { toast.error("No se pudo actualizar"); }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchQ = `${l.nombre} ${l.empresa} ${l.email} ${l.whatsapp}`.toLowerCase().includes(q.toLowerCase());
      const matchF = filter === "todos" || l.resultado === filter;
      return matchQ && matchF;
    });
  }, [leads, q, filter]);

  const exportCsv = () => {
    const headers = ["Fecha", "Nombre", "Empresa", "WhatsApp", "Email", "RFC", "Ingreso", "Tiempo", "Monto", "Destinos", "Buro", "Garantia", "Urgencia", "Resultado", "Producto", "Score", "Estado"];
    const rows = filtered.map((l) => [
      new Date(l.created_at).toLocaleString("es-MX"), l.nombre, l.empresa, l.whatsapp, l.email, l.rfc || "",
      LABELS.tipo_ingreso[l.tipo_ingreso], LABELS.tiempo_operando[l.tiempo_operando], LABELS.monto[l.monto],
      (l.destinos || []).join("; "), LABELS.historial_buro[l.historial_buro], LABELS.garantia[l.garantia],
      LABELS.urgencia[l.urgencia], RESULT_BADGE[l.resultado]?.t, l.producto_sugerido, l.score, ESTADO_LABEL[l.estado],
    ].map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `leads-brillo-${Date.now()}.csv`; a.click();
  };

  const statCards = stats ? [
    { i: Users, t: "Total leads", v: stats.total, c: "#04b0ba" },
    { i: TrendingUp, t: "Alto potencial", v: stats.alto_potencial, c: "#00C853" },
    { i: Zap, t: "Requieren análisis", v: stats.requiere_analisis, c: "#FFB800" },
    { i: Clock, t: "Nuevos sin atender", v: stats.nuevos, c: "#333333" },
  ] : [];

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-[#333333] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Brillo Finance" className="h-8 w-auto object-contain bg-white px-1.5 py-1 rounded-sm" />
            <span className="label-caps text-white/40 hidden sm:inline">· Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/60 hidden sm:inline">{user?.email}</span>
            <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#04b0ba] transition-colors">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Clientes precalificados</h1>
            <p className="text-sm text-[#52525B]">Información capturada en la landing de financiamiento.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 border border-[#E4E4E7] bg-white text-sm font-semibold px-4 py-2.5 hover:border-[#333333] transition-colors"><Download className="w-4 h-4" /> Exportar CSV</button>
            <button data-testid="refresh-button" onClick={load} className="inline-flex items-center gap-2 border border-[#E4E4E7] bg-white text-sm font-semibold px-4 py-2.5 hover:border-[#333333] transition-colors"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar</button>
          </div>
        </div>

        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E4E7] border border-[#E4E4E7] mb-6">
          {statCards.map((s) => (
            <div key={s.t} className="bg-white p-5" data-testid={`stat-${s.t}`}>
              <s.i className="w-5 h-5" style={{ color: s.c }} />
              <div className="font-display text-3xl font-bold mt-3">{s.v}</div>
              <div className="text-xs text-[#52525B] mt-1">{s.t}</div>
            </div>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, empresa, correo…"
              className="w-full pl-9 pr-4 py-2.5 border border-[#E4E4E7] bg-white outline-none focus:border-[#04b0ba] text-sm transition-colors" />
          </div>
          <div className="flex gap-1.5">
            {["todos", "alto_potencial", "requiere_analisis", "no_califica_aun"].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                className={`px-3 py-2.5 text-xs font-semibold border transition-colors ${filter === f ? "bg-[#333333] text-white border-[#333333]" : "bg-white border-[#E4E4E7] hover:border-[#333333]"}`}>
                {f === "todos" ? "Todos" : RESULT_BADGE[f].t}
              </button>
            ))}
          </div>
        </div>

        {/* table */}
        <div className="border border-[#E4E4E7] bg-white overflow-x-auto custom-scroll" data-testid="leads-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="border-b border-[#E4E4E7] text-left label-caps text-[10px]">
                <th className="p-4 font-bold">Cliente</th>
                <th className="p-4 font-bold">Contacto</th>
                <th className="p-4 font-bold">Monto</th>
                <th className="p-4 font-bold">Perfil</th>
                <th className="p-4 font-bold">Estado</th>
                <th className="p-4 font-bold">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-10 text-center text-[#52525B]">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center text-[#52525B]">Aún no hay leads que coincidan.</td></tr>
              ) : filtered.map((l) => {
                const badge = RESULT_BADGE[l.resultado] || {};
                return (
                  <tr key={l._id} className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors cursor-pointer" data-testid={`lead-row-${l._id}`} onClick={() => setSelected(l)}>
                    <td className="p-4">
                      <div className="font-semibold">{l.nombre}</div>
                      <div className="text-xs text-[#52525B]">{l.empresa}</div>
                      <div className="text-[11px] text-[#52525B] mt-0.5">{new Date(l.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="p-4">
                      <div>{l.email}</div>
                      <div className="text-xs text-[#52525B]">{l.whatsapp}</div>
                    </td>
                    <td className="p-4 whitespace-nowrap">{LABELS.monto[l.monto]}</td>
                    <td className="p-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${badge.c}15`, color: badge.c }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: badge.c }} /> {badge.t}
                      </span>
                      {l.degradado_buro && (
                        <span data-testid={`buro-flag-${l._id}`} title="Tenía puntaje de Alto potencial pero fue degradado por cuentas vencidas activas en Buró"
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#f05c3e]/10 text-[#f05c3e]">
                          <AlertTriangle className="w-3 h-3" /> Degradado por Buró
                        </span>
                      )}
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <select data-testid={`estado-select-${l._id}`} value={l.estado} onChange={(e) => updateEstado(l._id, e.target.value)}
                        className="border border-[#E4E4E7] bg-white text-xs px-2 py-1.5 outline-none focus:border-[#04b0ba]">
                        {ESTADOS.map((e) => <option key={e} value={e}>{ESTADO_LABEL[e]}</option>)}
                      </select>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <a data-testid={`wa-${l._id}`} href={`https://wa.me/52${l.whatsapp}?text=${encodeURIComponent(`Hola ${l.nombre}, soy tu asesor de Brillo Finance. Revisé tu precalificación y quiero platicarte tus opciones de crédito.`)}`}
                        target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#25D366] font-semibold text-xs hover:underline">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>

      {/* detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <motion.div initial={{ x: 400 }} animate={{ x: 0 }} transition={{ type: "tween", duration: 0.3 }} className="relative w-full max-w-md bg-white h-full overflow-y-auto custom-scroll">
            <div className="sticky top-0 bg-[#333333] text-white p-6 flex items-start justify-between">
              <div>
                <div className="font-display text-xl font-bold">{selected.nombre}</div>
                <div className="text-sm text-white/60">{selected.empresa}</div>
              </div>
              <button data-testid="close-detail" onClick={() => setSelected(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              {selected.degradado_buro && (
                <div data-testid="buro-alert" className="flex items-start gap-2 border border-[#f05c3e]/30 bg-[#f05c3e]/10 text-[#f05c3e] p-3 rounded-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p className="text-sm font-medium">Este perfil tenía puntaje de <b>Alto potencial</b>, pero se degradó a <b>Requiere análisis</b> por declarar <b>cuentas vencidas activas</b> en Buró. Revisar garantías y situación antes de presentar opciones.</p>
                </div>
              )}
              <Detail label="Resultado" value={RESULT_BADGE[selected.resultado]?.t} />
              <Detail label="Producto sugerido" value={selected.producto_sugerido} />
              <Detail label="Score" value={`${selected.score} / 125`} />
              <div className="h-px bg-[#E4E4E7]" />
              <Detail label="Correo" value={selected.email} />
              <Detail label="WhatsApp" value={selected.whatsapp} />
              <Detail label="RFC" value={selected.rfc || "—"} />
              <div className="h-px bg-[#E4E4E7]" />
              <Detail label="Tipo de ingreso" value={LABELS.tipo_ingreso[selected.tipo_ingreso]} />
              <Detail label="Tiempo operando" value={LABELS.tiempo_operando[selected.tiempo_operando]} />
              <Detail label="Monto solicitado" value={LABELS.monto[selected.monto]} />
              <Detail label="Destino" value={(selected.destinos || []).join(", ") || "—"} />
              <Detail label="Historial Buró" value={LABELS.historial_buro[selected.historial_buro]} />
              <Detail label="Garantía" value={LABELS.garantia[selected.garantia]} />
              <Detail label="Urgencia" value={LABELS.urgencia[selected.urgencia]} />
              <Detail label="Registrado" value={new Date(selected.created_at).toLocaleString("es-MX")} />
              <a href={`https://wa.me/52${selected.whatsapp}?text=${encodeURIComponent(`Hola ${selected.nombre}, soy tu asesor de Brillo Finance.`)}`} target="_blank" rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3 mt-4"><MessageCircle className="w-5 h-5" /> Contactar por WhatsApp</a>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <div className="label-caps mb-1">{label}</div>
    <div className="text-[#333333] font-medium">{value}</div>
  </div>
);
